/**
 * B2BCotationVersionsTimeline — Phase B (versioning cotations · mai 2026).
 *
 * Affiche la liste des versions PDF de la cotation sous forme de timeline
 * verticale (V plus récente en haut), avec pour chaque version :
 *   - Badge statut (draft / sent / validated / replaced / rejected)
 *   - Numéro V, date d'upload, utilisateur, taille fichier
 *   - Montant total de la version (si défini)
 *   - Commentaire / motif de remplacement
 *   - Boutons : Télécharger · Envoyer au client (si draft) · Supprimer (si draft)
 *
 * NB : seule la version `sent` ou `validated` la plus récente est exposée
 * au client via `/cotation/{token}`. Les drafts restent visibles côté admin
 * uniquement.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Download, Send, Trash2, Loader2, AlertCircle, Layers, CheckCircle2,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { API, auth, fmtDateTime } from './B2BConstants';

const VERSION_STATUS_LABELS = {
  draft: 'Brouillon (non envoyée)',
  sent: 'Envoyée au client',
  validated: 'Validée par le client',
  replaced: 'Remplacée',
  rejected: 'Refusée par le client',
};
const VERSION_STATUS_TONES = {
  draft: 'bg-zinc-100 text-zinc-700 border-zinc-300',
  sent: 'bg-blue-100 text-blue-800 border-blue-300',
  validated: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  replaced: 'bg-zinc-200 text-zinc-500 border-zinc-300',
  rejected: 'bg-red-100 text-red-800 border-red-300',
};

const formatBytes = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(2)} Mo`;
};

export const CotationVersionsTimeline = ({ orderId, canWrite, canDelete, onChanged }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // version_number en cours d'action
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/b2b/orders/${orderId}/cotation/versions`, { headers: auth() });
      // Tri décroissant (la plus récente en haut)
      setVersions([...r.data].sort((a, b) => b.version_number - a.version_number));
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur de chargement des versions');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const downloadVersion = async (v) => {
    setBusy(v.version_number);
    try {
      const r = await axios.get(
        `${API}/b2b/orders/${orderId}/cotation/versions/${v.version_number}/download`,
        { headers: auth(), responseType: 'blob' },
      );
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur téléchargement');
    } finally {
      setBusy(null);
    }
  };

  const sendVersion = async (v) => {
    if (!window.confirm(
      `Envoyer la cotation V${v.version_number} au client ?\n\n`
      + 'Le client recevra un nouveau lien sécurisé pour valider, demander une révision ou refuser. '
      + 'L\'ancien lien (si existant) sera invalidé.',
    )) return;
    setBusy(v.version_number);
    try {
      await axios.post(
        `${API}/b2b/orders/${orderId}/cotation/versions/${v.version_number}/send-for-validation`,
        {},
        { headers: auth() },
      );
      toast.success(`Cotation V${v.version_number} envoyée au client.`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setBusy(null);
    }
  };

  const deleteVersion = async (v) => {
    if (!window.confirm(`Supprimer définitivement le brouillon V${v.version_number} ?`)) return;
    setBusy(v.version_number);
    try {
      await axios.delete(
        `${API}/b2b/orders/${orderId}/cotation/versions/${v.version_number}`,
        { headers: auth() },
      );
      toast.success(`Brouillon V${v.version_number} supprimé.`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <div className="p-4 text-center text-zinc-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  }
  if (error) {
    return (
      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
        <AlertCircle className="w-4 h-4" /> {error}
      </div>
    );
  }
  if (versions.length === 0) {
    return (
      <div className="p-4 text-center bg-zinc-50 border border-dashed border-zinc-300 rounded-lg">
        <FileText className="w-7 h-7 mx-auto text-zinc-300 mb-1" />
        <p className="text-sm text-zinc-500">Aucune version PDF téléversée pour ce dossier.</p>
      </div>
    );
  }

  // Met en évidence la présence d'un draft non envoyé (rappel visuel pour l'admin)
  const pendingDrafts = versions.filter((v) => v.status === 'draft');

  return (
    <div className="space-y-3" data-testid="b2b-cotation-versions-timeline">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h4 className="text-sm font-bold text-zinc-700 flex items-center gap-1.5">
          <Layers className="w-4 h-4 text-indigo-600" />
          Versions ({versions.length})
        </h4>
        {pendingDrafts.length > 0 && (
          <Badge variant="outline" className="border-amber-400 bg-amber-50 text-amber-800 text-[11px]" data-testid="b2b-cotation-pending-draft-badge">
            ⏳ {pendingDrafts.length} brouillon{pendingDrafts.length > 1 ? 's' : ''} à envoyer
          </Badge>
        )}
      </div>

      <ol className="relative border-l-2 border-zinc-200 pl-4 space-y-3">
        {versions.map((v) => {
          const isBusy = busy === v.version_number;
          const tone = VERSION_STATUS_TONES[v.status] || VERSION_STATUS_TONES.draft;
          const isDraft = v.status === 'draft';
          const isActive = v.status === 'sent' || v.status === 'validated';
          return (
            <li
              key={v.version_number}
              className={`relative bg-white border rounded-lg p-3 ${isActive ? 'border-blue-300 shadow-sm' : 'border-zinc-200'}`}
              data-testid={`b2b-cotation-version-${v.version_number}`}
            >
              {/* Pastille temporelle */}
              <span className={`absolute -left-[22px] top-3 w-3 h-3 rounded-full border-2 ${isActive ? 'bg-blue-500 border-blue-200' : v.status === 'validated' ? 'bg-emerald-500 border-emerald-200' : 'bg-zinc-300 border-zinc-100'}`} />
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-bold text-zinc-900">V{v.version_number}</span>
                    <Badge variant="outline" className={`text-[10px] ${tone}`}>
                      {VERSION_STATUS_LABELS[v.status] || v.status}
                    </Badge>
                    {v.status === 'validated' && (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    )}
                  </div>
                  <p className="text-xs text-zinc-600 truncate" title={v.file_name}>{v.file_name}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">
                    {fmtDateTime(v.uploaded_at)} · par <strong>{v.uploaded_by_name || '—'}</strong>
                    {v.file_size ? ` · ${formatBytes(v.file_size)}` : ''}
                  </p>
                  {v.total_amount != null && (
                    <p className="text-xs text-zinc-700 mt-1">
                      Montant : <strong className="tabular-nums">{Number(v.total_amount).toLocaleString('fr-FR')} FCFA</strong>
                    </p>
                  )}
                  {v.comment && (
                    <p className="text-[11px] text-zinc-600 mt-1 italic">« {v.comment} »</p>
                  )}
                  {v.sent_at && (
                    <p className="text-[10px] text-zinc-400 mt-0.5">Envoyée au client : {fmtDateTime(v.sent_at)}</p>
                  )}
                  {v.validated_at && (
                    <p className="text-[10px] text-emerald-600 mt-0.5">Validée : {fmtDateTime(v.validated_at)}</p>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadVersion(v)}
                    disabled={isBusy}
                    className="h-7 text-xs"
                    data-testid={`b2b-cotation-version-download-${v.version_number}`}
                  >
                    {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                    Télécharger
                  </Button>
                  {isDraft && canWrite && (
                    <Button
                      size="sm"
                      onClick={() => sendVersion(v)}
                      disabled={isBusy}
                      className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid={`b2b-cotation-version-send-${v.version_number}`}
                    >
                      <Send className="w-3 h-3 mr-1" /> Envoyer V{v.version_number} au client
                    </Button>
                  )}
                  {isDraft && canDelete && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteVersion(v)}
                      disabled={isBusy}
                      className="h-7 text-xs border-red-200 text-red-700 hover:bg-red-50"
                      data-testid={`b2b-cotation-version-delete-${v.version_number}`}
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Supprimer
                    </Button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};
